"use client";
import useResponsiveSize from "@/hooks/useResponsiveSize";
import { useAutoTranslate } from "@/lib/i18n/useAutoTranslate";
import { Input, InputProps } from "antd";
import { useState } from "react";
import { IoSearch } from "react-icons/io5";
import { InputFieldProps } from "./types";

const InputField: React.FC<
  InputFieldProps & Omit<InputProps, "size" | "onChange" | "onBlur">
> = ({
  id,
  label,
  name,
  value,
  disabled,
  onChange,
  onBlur,
  size,
  placeholder,
  required,
  error,
  type = "text",
  errorMessage,
  touched,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const responsiveSize = useResponsiveSize();
  const tr = useAutoTranslate();
  const translatedLabel = tr(label);
  const translatedPlaceholder = tr(placeholder);
  const translatedErrorMessage = tr(errorMessage);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(value !== "");
    if (onBlur) onBlur(event);
  };

  const status = error ? "error" : undefined;

  return (
    <div className={`relative ${error ? "pb-5" : ""}`}>
      {label && (
        <label
          className={`${
            disabled ? "text-[#5B6B79]" : "text-text-primary"
          } font-medium text-xs md:text-sm`}
        >
          {translatedLabel}
          {required && (
            <span className="text-[#FF1F1F] ml-1" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      <div className="mt-1">
        {type === "password" ? (
          <Input.Password
            id={id}
            name={name}
            value={value}
            size={size || responsiveSize} // 👈 use responsive unless overridden
            placeholder={translatedPlaceholder}
            autoComplete="off"
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            disabled={disabled}
            status={status}
            {...rest}
            data-touched={touched ? "true" : "false"}
          />
        ) : type === "search" ? (
          <Input
            id={id}
            name={name}
            value={value}
            size={size || responsiveSize} // 👈 use responsive unless overridden
            placeholder={translatedPlaceholder}
            autoComplete="off"
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            disabled={disabled}
            status={status}
            prefix={<IoSearch className="text-gray-400" />}
            {...rest}
          />
        ) : (
          <Input
            id={id}
            type={type}
            name={name}
            value={value}
            size={size || responsiveSize} // 👈 use responsive unless overridden
            placeholder={translatedPlaceholder}
            autoComplete="off"
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={handleBlur}
            disabled={disabled}
            status={status}
            {...rest}
            data-touched={touched ? "true" : "false"}
          />
        )}
      </div>

      <div
        className={`text-[10px] text-[#FF1F1F] mt-1 absolute left-0 right-0 break-words leading-tight transition-all duration-300 ease-in-out ${
          error
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-95 -translate-y-1"
        }`}
      >
        {translatedErrorMessage}
      </div>
    </div>
  );
};

export default InputField;
